import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Crown, ExternalLink, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export function ModelCard({ model, isPremiumUser = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLocked = model.is_premium && !isPremiumUser;
  const requiresLogin = !user;

  const handleClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      toast.info('🚀 Recurso Premium estará disponível em breve!', { duration: 4000 });
      return;
    }
    if (requiresLogin) {
      e.preventDefault();
      toast.info('✨ Cadastre-se gratuitamente para ver os detalhes do modelo', { duration: 4000 });
      setTimeout(() => navigate('/register'), 600);
    }
  };

  const linkTo = isLocked || requiresLogin ? '#' : `/model/${model.id}`;

  return (
    <motion.div
      data-testid={`model-card-${model.id}`}
      whileHover={{ scale: 1.02 }}
      className="snap-start shrink-0 w-[280px] sm:w-[320px]"
    >
      <Link to={linkTo} onClick={handleClick}>
        <div className="bg-[#121212] rounded-lg overflow-hidden relative group cursor-pointer border border-[#27272A] hover:border-[#0066FF]/50 transition-all duration-300">
          <div className="aspect-video overflow-hidden relative">
            {model.images && model.images.length > 0 ? (
              <img
                src={model.images[0]}
                alt={model.title}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
            ) : (
              <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                <span className="text-zinc-600 text-sm">Sem imagem</span>
              </div>
            )}
            {isLocked && (
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center">
                <div className="text-center">
                  <Crown className="w-12 h-12 text-[#00FF99] mx-auto mb-2" />
                  <p className="text-white font-bold">Apenas Premium</p>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            {model.is_featured && (
              <div className="absolute top-3 left-3 bg-gradient-to-r from-[#0066FF] to-[#00FF99] text-black text-xs font-bold px-3 py-1 rounded-full">
                DESTAQUE
              </div>
            )}
          </div>
          <div className="p-4">
            <h3 className="text-white font-bold text-lg mb-1 line-clamp-1">{model.title}</h3>
            <p className="text-zinc-400 text-sm mb-3 line-clamp-2">{model.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-xs">
                <span className="text-[#00FF99] font-bold">🔥 {model.viral_score}</span>
                <span className="text-zinc-500">⏱ {model.print_time}</span>
              </div>
              {!isLocked && (
                <ExternalLink className="w-4 h-4 text-[#0066FF] opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function HorizontalScrollRow({ title, models, category, isPremiumUser }) {
  if (!models || models.length === 0) return null;

  return (
    <section data-testid={`category-${category}`} className="mb-12">
      <div className="mb-4 flex items-end justify-between">
        <Link
          to={`/category/${category}`}
          data-testid={`category-title-${category}`}
          className="group inline-flex items-center space-x-2 hover:text-[#00FF99] transition-colors"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight group-hover:text-[#00FF99] transition-colors">
            {title}
          </h2>
          <ChevronRight className="w-6 h-6 text-zinc-500 group-hover:text-[#00FF99] group-hover:translate-x-1 transition-all" />
        </Link>
        <Link
          to={`/category/${category}`}
          className="text-sm text-zinc-400 hover:text-[#0066FF] transition-colors hidden sm:inline"
        >
          Ver todos
        </Link>
      </div>
      <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-4">
        {models.map((model) => (
          <ModelCard key={model.id} model={model} isPremiumUser={isPremiumUser} />
        ))}
      </div>
    </section>
  );
}